import * as React from 'react';
import ImageModal from './ImageModal';
import SourceCodeModal from './SourceCodeModal';
import { Editor as TinyMceEditor } from '@tinymce/tinymce-react';
import { IAttachment } from '../../../features/orders/view/basicorderinfo/Types';

interface IEditorProps {
    content: string;
    attachments?: IAttachment[];
    files?: IImages[];
    height: number;
    onChange: (content: string) => void;
    onImageUploaded: (reference: string, filename: string) => void;
    onAttachmentUpload: (attachment: IAttachment) => void;
    onAttachmentDelete: (id: number) => void;
    addImageModal: boolean;
}

interface IImages {
    reference: any;
    filename: string;
}

interface IState {
    showImageModal: boolean;
    showAttachmentModal: boolean;
    showCodeSourceModal: boolean;
    attachmentsCount: number | null;
}

class Editor extends React.Component<IEditorProps, IState> {
    private currentEditor: any;

    private plugins = 'table link lists preview';
    private menubar = '';
    private toolbar = `
        undo redo | styleselect | bold italic underline | alignleft aligncenter alignright |
        bullist numlist outdent indent | insertimage | preview sourcecode | attachment
    `;

    private setup = (editor) => {
        if (this.props.addImageModal) {
            editor.ui.registry.addButton('insertimage', {
                icon: 'image',
                tooltip: 'Insert image',
                onAction: () => {
                    this.openImageModal(editor);
                }
            });
        }

        const addAttachments =
            Boolean(this.props.attachments) &&
            Boolean(this.props.onAttachmentUpload) &&
            Boolean(this.props.onAttachmentDelete);

        if (addAttachments) {
            const attachments = this.state.attachmentsCount;
            const attachmentsText = attachments > 0 ? { text: `${attachments}` } : {};

            editor.ui.registry.addButton('attachment', {
                icon: 'link',
                tooltip: 'Insert attachment',
                ...attachmentsText,
                onAction: () => {
                    this.setState({ showAttachmentModal: true });
                }
            });
        }

        editor.ui.registry.addButton('sourcecode', {
            icon: 'code',
            tooltip: 'Code source',
            onAction: () => {
                this.currentEditor = editor;
                this.setState({ showCodeSourceModal: true });
            }
        });
    };

    constructor(props: IEditorProps) {
        super(props);

        this.state = {
            showImageModal: false,
            showAttachmentModal: false,
            showCodeSourceModal: false,
            attachmentsCount: this.props.attachments ? this.props.attachments.length : null
        };

        this.insertImage = this.insertImage.bind(this);
        this.openImageModal = this.openImageModal.bind(this);
        this.closeImageModal = this.closeImageModal.bind(this);
        this.onChangeContent = this.onChangeContent.bind(this);
        this.closeSourceCodeModal = this.closeSourceCodeModal.bind(this);
        this.onAttachmentUpload = this.onAttachmentUpload.bind(this);
        this.onAttachmentDelete = this.onAttachmentDelete.bind(this);
    }

    onChangeContent(e: any) {
        let content = e.target.getContent();
        this.onChange(content);
    }

    onChange(content: string) {
        this.props.onChange(content);
    }

    insertImage(imageReference: string, width: number, height: number) {
        const { apiUrl } = window.appConfig;
        const img = `<img height="${height}px" width="${width}px" src="${apiUrl}/file/${imageReference}">`;
        this.currentEditor.insertContent(img);
    }

    onAttachmentUpload(attachment: IAttachment) {
        this.setState({ attachmentsCount: this.state.attachmentsCount + 1 });
        this.props.onAttachmentUpload(attachment);
    }

    onAttachmentDelete(id: number) {
        this.setState({ attachmentsCount: this.state.attachmentsCount - 1 });
        this.props.onAttachmentDelete(id);
    }

    render() {
        return (
            <div className="RichEditor-root">
                <TinyMceEditor
                    key={`${this.state.attachmentsCount} attachments`}
                    initialValue={this.props.content}
                    onChange={this.onChangeContent}
                    init={{
                        plugins: this.plugins,
                        menubar: this.menubar,
                        toolbar: this.toolbar,
                        setup: this.setup,
                        height: this.props.height.toString(),
                        relative_urls: false,
                        remove_script_host: false
                    }}
                />

                {this.state.showImageModal && (
                    <ImageModal
                        files={this.props.files}
                        onClose={this.closeImageModal}
                        onImageUploaded={this.props.onImageUploaded}
                    />
                )}

                {this.state.showAttachmentModal && (
                    <ImageModal
                        files={this.props.attachments}
                        onClose={() => this.setState({ showAttachmentModal: false })}
                        onImageUploaded={this.onAttachmentUpload}
                        onDelete={this.onAttachmentDelete}
                        asAttachment={true}
                    />
                )}

                {this.state.showCodeSourceModal && (
                    <SourceCodeModal
                        content={this.props.content}
                        close={this.closeSourceCodeModal}
                    />
                )}
            </div>
        );
    }

    openImageModal(editor) {
        this.currentEditor = editor;
        this.setState({ showImageModal: true });
    }

    closeImageModal(imageReference: string, width: number, height: number) {
        this.setState({ showImageModal: false });

        if (imageReference) {
            this.insertImage(imageReference, width, height);
        }
    }

    closeSourceCodeModal(content: string) {
        this.setState({ showCodeSourceModal: false });

        if (content) {
            this.onChange(content);
            this.currentEditor.setContent(content);
        }
    }
}

export default Editor;
